import initialState from './initialState';
import {
  RECIEVE_SEGMENT_CODES,
  RECIEVE_SEGMENT_DETAILS,
  RESET_SELECTED_SEGMENT_DETAIL_SUVCS,
  RESET_SEGMENT_CODES,
  SELECT_SEGMENT_DETAIL_SUVC_ASSOCIATION,
  SELECT_SEGMENT_DETAIL_VENDOR_ASSOCIATION,
  SEGMENT_DETAIL_SEARCH_VENDORS,
  RECEIVE_ALL_VENDORS_FOR_SEGMENT_DETAIL,
  REVERT_SEGMENT_DETAIL_UNSAVED_VENDOR_SELECTIONS,
  FETCH_SEGMENT_VERSIONS,
  RECEIVE_SEGMENT_VERSIONS,
  SEGMENT_VERSIONS_ERROR,
  SET_SELECTED_VERSION,
  RESET_SELECTED_VERSION,
  RECIEVE_SEGMENT_VENDOR_MAPPING,
  RESET_SEGMENT_DETAILS_AND_COMMENTS,
  SET_SELECTED_SEGMENT_CODE,
  RESET_EVENT_TYPE,
  RECEIVE_SEGMENT_DETAILS_BY_SEGMENT_VERSION
} from '../actions/segmentDataActions';
import stringMerge from '../../util/stringMerge';
import removeByKey from '../../util/removeByKey';
import { SEGMENT_DETAIL_MODES, SUCCESS_EVENTS } from '../../helpers/constants';

function getSelectedSuvc(suvcCode, filterState) {
  const selectedsuvc = (filterState?.suvcMappingList || []).find((suvc) => suvc.code === suvcCode) || null;
  if (!selectedsuvc) {
    return null;
  }

  return {
    code: selectedsuvc.code,
    name: selectedsuvc.name,
    vendors: (selectedsuvc.vendors || []).map((vendor) => ({
      code: vendor.code,
      name: vendor.name
    }))
  };
}

function getApplicableSuvcList(code, filterState) {
  const selectedSuvcs = filterState?.selectedSuvcs || {};
  const searchedSuvcList = filterState?.searchedSuvcList || {};

  if (Object.hasOwn(searchedSuvcList, code)) {
    return { ...searchedSuvcList };
  }

  if (Object.hasOwn(selectedSuvcs, code)) {
    return { ...selectedSuvcs };
  }

  return { ...selectedSuvcs };
}

function getSelectedOrder(code, selectedSuvcArray) {
  return selectedSuvcArray.includes(code) ? [...selectedSuvcArray] : [...selectedSuvcArray, code];
}

function setVendorsAndSuvcCode(suvc, data, selectedVendors, arr, suvcObj, suvcMap) {
  let isVendorsMatched = false;
  if (suvc.vendors) {
    suvc.vendors.forEach((vendor) => {
      if (stringMerge(vendor.code, vendor.name, ' - ').includes(data.value.toUpperCase())) {
        isVendorsMatched = true;
        const vendorObj = selectedVendors
          && selectedVendors.length > 0
          && selectedVendors.includes(vendor.code)
          ? { ...vendor, selected: true }
          : { ...vendor };

        arr.push(vendorObj);
      }
    });
  }
  suvcObj.vendors = arr;
  if (isVendorsMatched) {
    suvcMap[suvc.code] = suvcObj;
  }
}

function mapSuvc(data, state, suvcMap) {
  data.suvcMappingList.forEach((suvc) => {
    const isSuvcSelected = state.selectedSuvcs && Object.keys(state.selectedSuvcs).length > 0
      && Object.keys(state.selectedSuvcs).includes(suvc.code);

    const suvcObj = isSuvcSelected
      ? { ...state.selectedSuvcs[suvc.code] }
      : { ...suvc };

    if (stringMerge(suvc.code, suvc.name, ' - ').includes(data.value.toUpperCase())) {
      suvcMap[suvc.code] = suvcObj;
    } else {
      const arr = [];
      let selectedVendors;
      if (isSuvcSelected) {
        selectedVendors = suvcObj.vendors.filter((value) => value.selected === true).map((value) => value.code);
      }
      setVendorsAndSuvcCode(suvc, data, selectedVendors, arr, suvcObj, suvcMap);
    }
  });
}

function setVendorSelectionStatus(vendor, data) {
  if (vendor.code === data.row.code) {
    vendor.selected = data.value;
  } else {
    vendor.selected = false;
  }
}

function setSelectionStatus(suvcObj, state, data) {
  let isAllSelected = true;
  let atLeastOneSelected = false;
  if (suvcObj.vendors) {
    suvcObj.vendors.forEach((vendor) => {
      if (Object.keys(state.selectedSuvcs).includes(suvcObj.code)) {
        if (vendor.code === data.row.code) {
          vendor.selected = data.value;
        }
        isAllSelected = isAllSelected && vendor.selected;
        atLeastOneSelected = atLeastOneSelected || vendor.selected;
      } else {
        setVendorSelectionStatus(vendor, data);
        isAllSelected = isAllSelected && vendor.selected;
        atLeastOneSelected = atLeastOneSelected || vendor.selected;
      }
    });
  }
  suvcObj.selected = isAllSelected;
  suvcObj.minOneSelected = atLeastOneSelected;
}

const mapExistingComments = (comments) => (
  (comments ? Object.values(comments) : [])
    .filter(Boolean)
    .map((comment, index) => ({
      ...comment,
      id: `${index}-${comment.message || ''}`,
    }))
);

export default function segmentDataReducer(state, { type, data }) {
  if (typeof state === 'undefined') {
    return initialState.segmentDetailMaintenanceData;
  }

  switch (type) {
    case RECIEVE_SEGMENT_CODES: {
      const { segmentCodes, vendor } = data;
      return { ...state, segmentCodes, selectedVendorCode: vendor };
    }
    case RECEIVE_SEGMENT_DETAILS_BY_SEGMENT_VERSION:
    case RECIEVE_SEGMENT_DETAILS: {
      const { segmentDetails, segmentCode } = data;
      const { suvcs = [] } = segmentDetails || {};

      const associatedSuvcs = suvcs
        .map((suvcObj) => {
          const suvc = getSelectedSuvc(suvcObj.code, state);
          if (!suvc) return null;

          const associatedVendorCodes = (suvcObj.vendors || []).map((vendor) => (
            typeof vendor === 'string' ? vendor : vendor.code
          ));

          return { suvc, associatedVendorCodes };
        }).filter(Boolean);

      const existingComments = mapExistingComments(segmentDetails?.comments);

      const selectedSuvcs = associatedSuvcs.reduce((acc, { suvc }) => {
        acc[suvc.code] = suvc;
        return acc;
      }, {});

      const updatedState = { ...state, selectedSuvcs };

      associatedSuvcs.forEach(({ suvc, associatedVendorCodes }) => {
        const selectedVendors = (suvc.vendors || []).filter((vendor) => (
          associatedVendorCodes.includes(vendor.code)
        ));

        selectedVendors.forEach((selectedVendor) => {
          const row = {
            code: selectedVendor.code,
            name: selectedVendor.name,
            suvc
          };
          setSelectionStatus(suvc, updatedState, { row, value: true });
        });
      });

      const updatedVersion = {
        value: segmentDetails?.version,
        label: segmentDetails?.version
      };

      const initialSelectedSuvcs = JSON.parse(JSON.stringify(selectedSuvcs));
      const selectedOrder = associatedSuvcs.reduce((acc, { suvc }) => getSelectedOrder(suvc.code, acc),
        state.selectedOrder || []);
      const eventType = (data?.eventType === SUCCESS_EVENTS.FETCH_NEWLY_CREATED_SEGMENT_DETAILS
                      && data.eventType)
                    || '';
      return {
        ...updatedState,
        segmentDetails,
        selectedSegmentCode: segmentCode,
        selectedOrder,
        existingComments,
        initialSelectedSuvcs,
        selectedVersion: updatedVersion,
        selectedFirstVendor: data?.selectedFirstVendor ?? '',
        eventType,
        searchedSuvcList: {},
      };
    }
    case RECEIVE_ALL_VENDORS_FOR_SEGMENT_DETAIL: {
      return { ...state, suvcMappingList: data.suvcs, loading: false };
    }
    case RESET_SELECTED_SEGMENT_DETAIL_SUVCS: {
      return {
        ...state, suvcList: {}, selectedOrder: [], selectedSuvcs: {}, searchedSuvcList: {}, segmentDetails: {}, initialSelectedSuvcs: {}, selectedSegmentCode: ''
      };
    }
    case RESET_SEGMENT_CODES: {
      return { ...state, segmentCodes: [], selectedVendorCode: '' };
    }
    case RESET_SEGMENT_DETAILS_AND_COMMENTS: {
      return { ...state, segmentDetails: {}, existingComments: [] };
    }
    case SET_SELECTED_VERSION: {
      return { ...state, selectedVersion: data };
    }
    case SET_SELECTED_SEGMENT_CODE: {
      return { ...state, selectedSegmentCode: data };
    }
    case RESET_SELECTED_VERSION: {
      return { ...state, selectedVersion: {} };
    }
    case RESET_EVENT_TYPE: {
      return { ...state, eventType: '', selectedFirstVendor: '' };
    }
    case REVERT_SEGMENT_DETAIL_UNSAVED_VENDOR_SELECTIONS: {
      const selectedSuvcs = JSON.parse(JSON.stringify(state.initialSelectedSuvcs || {}));
      return { ...state, selectedSuvcs };
    }
    case SELECT_SEGMENT_DETAIL_SUVC_ASSOCIATION: {
      const suvcList = getApplicableSuvcList(data.row.code, state);
      const suvcObj = suvcList[data.row.code];
      suvcObj.vendors.forEach((vendor) => {
        vendor.selected = data.value;
      });
      suvcObj.selected = data.value;
      suvcObj.minOneSelected = data.value;
      let selectedSuvcs;
      let selectedOrder;

      if (suvcObj.selected || data.mode === SEGMENT_DETAIL_MODES.EDIT) {
        selectedSuvcs = { ...state.selectedSuvcs, [suvcObj.code]: suvcObj };
        selectedOrder = getSelectedOrder(suvcObj.code, state.selectedOrder);
      } else {
        selectedSuvcs = removeByKey(state.selectedSuvcs, suvcObj.code);
        selectedOrder = state.selectedOrder.filter((code) => code !== suvcObj.code);
      }
      return {
        ...state, suvcList, selectedSuvcs, selectedOrder, minOneSelected: suvcObj.minOneSelected
      };
    }
    case SELECT_SEGMENT_DETAIL_VENDOR_ASSOCIATION: {
      const suvcList = getApplicableSuvcList(data.row.suvc.code, state);
      const suvcObj = suvcList[data.row.suvc.code];

      setSelectionStatus(suvcObj, state, data);
      let selectedSuvcs;
      let selectedOrder;

      if (suvcObj.minOneSelected || data.mode === SEGMENT_DETAIL_MODES.EDIT) {
        selectedSuvcs = { ...state.selectedSuvcs, [suvcObj.code]: suvcObj };
        selectedOrder = getSelectedOrder(suvcObj.code, state.selectedOrder);
      } else {
        selectedSuvcs = removeByKey(state.selectedSuvcs, suvcObj.code);
        selectedOrder = state.selectedOrder.filter((code) => code !== suvcObj.code);
      }
      return {
        ...state, suvcList, selectedSuvcs, selectedOrder, minOneSelected: suvcObj.minOneSelected
      };
    }
    case SEGMENT_DETAIL_SEARCH_VENDORS: {
      if (data.suvcMappingList && Object.keys(data.suvcMappingList).length > 0) {
        const suvcMap = {};
        mapSuvc(data, state, suvcMap);
        return {
          ...state,
          suvcList: suvcMap,
          selectedSuvcs: { ...state.selectedSuvcs },
          selectedOrder: [...state.selectedOrder],
          searchedSuvcList: suvcMap,
        };
      }
      return state;
    }
    case FETCH_SEGMENT_VERSIONS: {
      return {
        ...state,
        versionError: null,
      };
    }

    case RECEIVE_SEGMENT_VERSIONS: {
      return {
        ...state,
        versionData: data,
      };
    }

    case SEGMENT_VERSIONS_ERROR: {
      return {
        ...state,
        versionError: data,
        versionData: {},
      };
    }

    case RECIEVE_SEGMENT_VENDOR_MAPPING: {
      return { ...state, segmentVendorMapping: data };
    }

    default:
      return state;
  }
}
